import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGraphColors } from "@core";
import type { ChartSeriesData } from "@core/apis/shared";

import {
  BaseSignatureDiagramChart,
  SignatureSeriesData,
  SignatureYAxisConfig,
} from "../charts/BaseSignatureDiagramChart";
import {
  buildWeatherMap,
  filterToDateRange,
  toSignatureDataPoints,
} from "../charts/signatureDiagramUtils";
import type { DateRange, Resolution } from "../shared/types";

type EnergySignatureDiagramContainerProps = {
  data: ChartSeriesData[];
  dateRange: DateRange;
  resolution?: Resolution;
  isLoading?: boolean;
  chartHeight?: number;
  primarySeriesName?: string;
  primarySeriesUnit?: string;
  secondarySeriesName?: string;
  secondarySeriesUnit?: string;
};

/**
 * EnergySignatureDiagramContainer - Container for energy consumption signature diagrams
 *
 * Transforms meter data into scatter plot series (value vs outdoor temperature).
 * Primary series: heat_energy (or radiator_supply_temp fallback).
 * Secondary series: radiator_supply_temp (when both available).
 */
export const EnergySignatureDiagramContainer: React.FC<EnergySignatureDiagramContainerProps> = ({
  data = [],
  dateRange,
  resolution = "hourly",
  isLoading = false,
  chartHeight,
  primarySeriesName,
  primarySeriesUnit = "kWh",
  secondarySeriesName,
  secondarySeriesUnit = "°C",
}) => {
  const { t } = useTranslation(["extendView", "propai_properties"]);
  const graphColors = useGraphColors();

  const primaryName = primarySeriesName || t("text_heat_energy");
  const secondaryName = secondarySeriesName || t("text_supply_temperature");

  // Transform meter data into signature data points
  const signatureData = useMemo(() => {
    const heatEnergyComponent = data.find((comp) => comp.component === "heat_energy");
    const radiatorTempComponent = data.find(
      (comp) => comp.component === "radiator_system_supply_temp"
    );
    const outdoorTempComponent = data.find((comp) => comp.component === "outdoor_temp");

    const weatherDataMap = outdoorTempComponent?.data
      ? buildWeatherMap(outdoorTempComponent.data, resolution)
      : new Map<string, number>();

    const heatEnergyInRange = heatEnergyComponent
      ? filterToDateRange(heatEnergyComponent.data, dateRange)
      : [];
    const radiatorTempInRange = radiatorTempComponent
      ? filterToDateRange(radiatorTempComponent.data, dateRange)
      : [];

    const transform = (d: { datetime: string; value: number }[]) =>
      toSignatureDataPoints(d, weatherDataMap, resolution);

    const primaryData =
      heatEnergyInRange.length > 0 ? transform(heatEnergyInRange) : transform(radiatorTempInRange);
    const secondaryData =
      heatEnergyInRange.length > 0 && radiatorTempInRange.length > 0
        ? transform(radiatorTempInRange)
        : [];

    return { primaryData, secondaryData };
  }, [data, resolution, dateRange]);

  // Map to SignatureSeriesData[]
  const series = useMemo((): SignatureSeriesData[] => {
    const result: SignatureSeriesData[] = [];

    if (signatureData.primaryData.length > 0) {
      result.push({
        uid: "primary",
        name: primaryName,
        data: signatureData.primaryData,
        unit: primarySeriesUnit,
        color: graphColors[0],
        yAxisIndex: 0,
        visible: true,
      });
    }

    if (signatureData.secondaryData.length > 0) {
      result.push({
        uid: "secondary",
        name: secondaryName,
        data: signatureData.secondaryData,
        unit: secondarySeriesUnit,
        color: graphColors[1],
        yAxisIndex: 1,
        visible: true,
      });
    }

    return result;
  }, [signatureData, primaryName, secondaryName, primarySeriesUnit, secondarySeriesUnit]);

  // Y-axis configs
  const yAxisConfigs = useMemo((): SignatureYAxisConfig[] => {
    const configs: SignatureYAxisConfig[] = [
      {
        title: { text: `${primaryName} (${primarySeriesUnit})` },
        labels: { align: "right", x: -15 },
      },
    ];

    if (signatureData.secondaryData.length > 0) {
      configs.push({
        title: { text: `${secondaryName} (${secondarySeriesUnit})` },
        labels: { align: "right", x: -25 },
      });
    }

    return configs;
  }, [primaryName, secondaryName, primarySeriesUnit, secondarySeriesUnit, signatureData]);

  return (
    <BaseSignatureDiagramChart
      series={series}
      yAxisConfigs={yAxisConfigs}
      chartHeight={chartHeight}
      chartId="energy-signature"
      resolution={resolution}
      isLoading={isLoading}
      allowSeriesToggle={true}
      yAxisLayout="side-by-side"
    />
  );
};
